import { FC, Fragment, useMemo } from 'react';

import { FULL_PRICE_FIELD_NAME, MONTHLY_PRICE_FIELD_NAME, SKIP_FACETS_FOR_FIELDS } from '../../constants';
import { FacetPrice, FacetStats, SearchFacet, SearchFilters, SearchFilterValue } from '../../model';
import { isFilterHierarchy, isFilterRange, isFilterTerms } from '../../utils/typeGuards';
import { FacetExpansionProvider } from '../FacetItem/FacetExpansionProvider';
import { HierarchyFacet } from './HierarchyFacet';
import { PriceFacet } from './PriceFacet';
import { RangeFacet } from './RangeFacet';
import { StatsFacet } from './StatsFacet';
import { TermsFacet } from './TermsFacet';

type Props = {
    facets: SearchFacet[];
    filters: SearchFilters;
    onChange: (fieldName: string, filter: SearchFilterValue) => void;
    showCount?: boolean;
};

const DEFAULT_EXPANDED_FACETS_COUNT = 3;

const isPriceStatsFacet = (facet: SearchFacet): facet is FacetStats =>
    facet.type === 'stats' && (facet.key === FULL_PRICE_FIELD_NAME || facet.key === MONTHLY_PRICE_FIELD_NAME);

const isFacetFinite = (facet: SearchFacet) => {
    switch (facet.type) {
        case 'hierarchy':
        case 'terms':
        case 'range':
            return facet.items.length > 0;
        case 'stats':
            return facet.min !== facet.max;
        default:
            return false;
    }
};

const createPriceFacet = (facet: FacetStats): FacetPrice => ({
    type: 'price',
    key: 'price',
    label: facet.label,
    monthlyPrice: facet.key === MONTHLY_PRICE_FIELD_NAME ? facet : undefined,
    fullPrice: facet.key === FULL_PRICE_FIELD_NAME ? facet : undefined,
});

const updatePriceFacet = (priceFacet: FacetPrice, facet: FacetStats) => {
    if (facet.key === MONTHLY_PRICE_FIELD_NAME) {
        priceFacet.monthlyPrice = facet;
    } else {
        priceFacet.fullPrice = facet;
    }
};

export const Facets: FC<Props> = ({ facets, filters, onChange, showCount }) => {
    const normalizedFilters = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { _and, ...baseFilters } = filters;

        return (_and || []).reduce((acc, filter) => ({ ...acc, ...filter }), baseFilters);
    }, [filters]);

    const mappedFacets = useMemo(() => {
        const mapped: (SearchFacet | FacetPrice)[] = [];

        facets.forEach((facet) => {
            if (SKIP_FACETS_FOR_FIELDS.includes(facet.key)) {
                return;
            }
            // Special case for price facet, monthly and full prices should be combined into one
            if (isPriceStatsFacet(facet)) {
                const priceFacet = mapped.find((mappedFacet) => mappedFacet.type === 'price');
                if (priceFacet) {
                    updatePriceFacet(priceFacet, facet);
                } else {
                    mapped.push(createPriceFacet(facet));
                }

                return;
            }
            // Hiding facets which does not have any entries
            if (isFacetFinite(facet)) {
                mapped.push(facet);
            }
        });

        return mapped;
    }, [facets]);

    const priceTypeFieldName = normalizedFilters?.[FULL_PRICE_FIELD_NAME]
        ? FULL_PRICE_FIELD_NAME
        : MONTHLY_PRICE_FIELD_NAME;
    const priceFilter = normalizedFilters?.[priceTypeFieldName];
    const initialExpandedFacetIds = useMemo(
        () => mappedFacets.slice(0, DEFAULT_EXPANDED_FACETS_COUNT).map(({ key }) => key),
        [mappedFacets],
    );

    return (
        <FacetExpansionProvider initialExpandedFacetIds={initialExpandedFacetIds}>
            {mappedFacets.map((facet) => {
                const filter = normalizedFilters[facet.key];

                return (
                    <Fragment key={facet.key}>
                        {facet.type === 'hierarchy' && (
                            <HierarchyFacet
                                facet={facet}
                                filter={isFilterHierarchy(filter) ? filter : undefined}
                                showCount={showCount}
                                onChange={onChange}
                            />
                        )}
                        {facet.type === 'terms' && (
                            <TermsFacet
                                facet={facet}
                                filter={isFilterTerms(filter) ? filter : undefined}
                                showCount={showCount}
                                onChange={onChange}
                            />
                        )}
                        {facet.type === 'stats' && (
                            <StatsFacet
                                facet={facet}
                                filter={isFilterRange(filter) ? filter : undefined}
                                onChange={onChange}
                            />
                        )}
                        {facet.type === 'range' && (
                            <RangeFacet
                                facet={facet}
                                filter={isFilterRange(filter) ? filter : undefined}
                                showCount={showCount}
                                onChange={onChange}
                            />
                        )}
                        {facet.type === 'price' && (
                            <PriceFacet
                                facet={facet}
                                priceTypeFieldName={priceTypeFieldName}
                                filter={isFilterRange(priceFilter) ? priceFilter : undefined}
                                onChange={onChange}
                            />
                        )}
                    </Fragment>
                );
            })}
        </FacetExpansionProvider>
    );
};
